// @flow
import React from 'react';
import { FormField } from 'semantic-ui-react';
import { getIn, useFormikContext } from 'formik';
import { flushSync } from 'react-dom';
import { useIsUserAdmin } from '@src/custom-hooks/useIsUserAdmin';

import {
  StyledCheckboxWithAccessibility,
  StyledDatepickerWithAccessibility,
  StyledDatePickerWrapper,
  StyledDropdownWithAccessibility,
  StyledDropdownWithAccessibilityForMultiSelectWithCheckboxes,
  StyledFormikErrorMessage,
  StyledInputWithAccessibility,
  StyledLabel,
  StyledSpan,
} from '../common-utils/styledComponents';
import { doesArrayContainAll, getDropdownOptionsFromArray } from '../common-utils/utils';
import { ALL_IN_SMALL_CASE, NO, YES } from '../constants';

import {
  getAllEnabledOptionsNamesFromAllOptions,
  getFormattedOptionsFromOptionsArray,
} from './utils';
import { Field } from './formikSUIWrapper';
import { BOOLEAN_SELECT_INPUT_TYPE, TEXT_NUMBER_INPUT_TYPE } from './consts';

const LabelContainer = ({ fieldDisplayName, tooltip, isMandatory }) => (
  <StyledLabel>
    {fieldDisplayName}
    {isMandatory && <strong style={{ color: 'red', fontSize: '1.2em' }}>*</strong>}
    {tooltip}
  </StyledLabel>
);

export const getFieldNameByFollowingTheGivenPathInFormikValues = (
  pathToFollowInValues,
  colBackendName
) => {
  if (pathToFollowInValues.length > 0) {
    return `${pathToFollowInValues.join('.')}.${colBackendName}`;
  }

  return colBackendName;
};

export const TextField = ({
  fieldName,
  fieldDisplayName,
  icon,
  onChange = () => {},
  tooltip = null,
  addLabel = true,
  addErrorMessage = true,
  errorMessagePosition = null,
  showErrorsOnlyWhenTouched = true,
  pathToFollowInValues = [],
  isMandatory = false,
  ...rest
}) => {
  // For Number Fields,add step="any"

  const stepProp = rest?.type === TEXT_NUMBER_INPUT_TYPE ? { step: 'any' } : {};

  const fieldNameToUse = getFieldNameByFollowingTheGivenPathInFormikValues(
    pathToFollowInValues,
    fieldName
  );

  return (
    <FormField>
      {addLabel && (
        <LabelContainer
          fieldDisplayName={fieldDisplayName}
          isMandatory={isMandatory}
          tooltip={tooltip}
        />
      )}
      <Field
        component={StyledInputWithAccessibility}
        icon={icon}
        name={fieldNameToUse}
        onChange={onChange}
        placeholder={fieldDisplayName}
        {...stepProp}
        {...rest}
      />
      {addErrorMessage && (
        <StyledFormikErrorMessage
          name={fieldNameToUse}
          position={errorMessagePosition}
          showErrorsOnlyWhenTouched={showErrorsOnlyWhenTouched}
        />
      )}
    </FormField>
  );
};

export const FileField = ({
  fieldName,
  fieldDisplayName,
  onChange = () => {},
  tooltip = null,
  addLabel = true,
  addErrorMessage = true,
  icon = null,
  accept = null,
  ...rest
}) => (
  <FormField>
    {addLabel && <LabelContainer fieldDisplayName={fieldDisplayName} tooltip={tooltip} />}

    <Field
      accept={accept}
      clearable={true}
      component={StyledInputWithAccessibility}
      icon={icon}
      name={fieldName}
      onChange={onChange}
      placeholder={fieldDisplayName}
      type="file"
      value={undefined}
      {...rest}
    />
    {addErrorMessage && <StyledFormikErrorMessage name={fieldName} />}
  </FormField>
);

export const DateField = ({
  fieldName,
  fieldDisplayName,
  onChange = () => {},
  tooltip = null,
  addLabel = true,
  addErrorMessage = true,
  isMandatory = false,
  ...rest
}) => (
  <FormField>
    {addLabel && (
      <LabelContainer
        fieldDisplayName={fieldDisplayName}
        isMandatory={isMandatory}
        tooltip={tooltip}
      />
    )}

    <StyledDatePickerWrapper>
      <Field
        clearOnSameDateClick={false}
        clearable={false}
        component={StyledDatepickerWithAccessibility}
        datePickerOnly={true}
        icon={false}
        name={fieldName}
        onChange={onChange}
        placeholder={fieldDisplayName}
        {...rest}
      />
      {addErrorMessage && <StyledFormikErrorMessage name={fieldName} />}
    </StyledDatePickerWrapper>
  </FormField>
);

const getNewSelectedOptions = (optionValue, options, selectedOptions) => {
  if (optionValue === 'select-all-individual') {
    // If this option is clicked when there are a few already selected options, then, we need to deselect everything
    if (selectedOptions.length > 0) {
      return [];
    }
    // Otherwise, we need to select everything
    return ['all'];
  }

  if (optionValue === 'select-all-enabled') {
    // Check if some options are selected, because selected options will only be a subset of enabled options
    if (selectedOptions.length > 0) {
      // If some options are selected, deselect all options
      return [];
    } else {
      // Get all enabled options (those without shouldNotBeSelectable)
      return getAllEnabledOptionsNamesFromAllOptions(options);
    }
  }

  // If we are here, then, it means, we are dealing with an individual element

  // First, use the actual selected values, aka, expand "all" into individual components
  let selectedOptionsToUse = [...selectedOptions];

  if (selectedOptions.includes('all')) {
    selectedOptionsToUse = [...options.map((x) => x.name)];
  }

  if (selectedOptionsToUse.includes(optionValue)) {
    // If the clicked option is already selected, then, remove it from the list
    return selectedOptionsToUse.filter((value) => value !== optionValue);
  } else {
    //  Otherwise, we need to add it to the existing list.
    // A corner case is when the addition of this option means that "all" options are individually selected
    // Here, check if adding this particular elem makes it "all"
    if (selectedOptionsToUse.length + 1 === options.length) {
      return ['all'];
    }
    return [...selectedOptionsToUse, optionValue];
  }
};

const getIcon = (disabled, isSelected, displayName, selectedOptions, options) => {
  if (disabled) {
    // In disabled state, we should not show the checkboxes
    return null;
  }

  /*
   * For Select All Available option, show icon based on enabled options state
   *
   * Select All Available icon is only applicable for Non-Admins for field, which means
   * selected options will only be a subset of enabled options.
   *
   * If selected options length is zero(this will also handle if enabled options length is zero), then, show square outline
   * If enabled options length is equal to selected options length, then, show check square outline
   * Otherwise, show minus square outline
   */
  if (displayName === 'Select All Available') {
    // If no options are selected, show square outline. This will also handle if enabled options length is zero
    if (selectedOptions.length === 0) {
      return 'square outline';
    }

    // Get all enabled options
    const enabledOptionNames = getAllEnabledOptionsNamesFromAllOptions(options);

    const areAllEnabledOptionsSelected = selectedOptions.length === enabledOptionNames.length;

    if (areAllEnabledOptionsSelected) {
      return 'check square outline';
    }

    return 'minus square outline';
  }

  // For Select All option, show icon based on the different states
  if (displayName === 'Select All') {
    // Case 1: All of them are selected, then show tick mark
    if (selectedOptions.includes('all')) {
      return 'check square outline';
    }

    // Case 2: None of them are selected, don't show tick mark
    if (selectedOptions.length === 0) {
      return 'square outline';
    }

    // Case 3: A few are selected, show that minus sign
    return 'minus square outline';
  } else {
    // For an individual item, we need to show Checked Square if it is selected individually or "all" is selected
    return selectedOptions.includes('all') || isSelected
      ? 'check square outline'
      : 'square outline';
  }
};

const getSingleSelectedOptionDisplayName = (selectedOptions, optionsToConsider) => {
  const singleSelectedOption = selectedOptions[0];
  return singleSelectedOption === 'all'
    ? 'ALL'
    : optionsToConsider.find((x) => x.name === singleSelectedOption).display_name;

  // Note, the above line will be more performant if we use a Map.
  // I will change it if we see a performance degradation currently not worth the refactor
};

const getMultiSelectOptionsIncludingAllAndEnableAllIfApplicable = (optionsToUse, isUserAdmin) => {
  const selectAllOption = {
    name: 'select-all-individual',
    display_name: 'Select All',
    shouldNotBeSelectable: !isUserAdmin, // For an Admin, Select All option should be enabled and for a non-admin, it should be disabled
  };

  const selectAllEnabledOption = {
    name: 'select-all-enabled',
    display_name: 'Select All Available',
  };

  if (!isUserAdmin) {
    return [selectAllOption, selectAllEnabledOption, ...optionsToUse];
  }

  return [selectAllOption, ...optionsToUse];
};
export const MultiSelectFieldWithCheckboxes = ({
  fieldName,
  fieldDisplayName,
  tooltip,
  options,
  addLabel = true,
  addErrorMessage = true,
  addPlaceHolder = true,
  pathToFollowInValues = [],
  showErrorsOnlyWhenTouched = true,
  disabled = false,
  onChange = () => {},
  ...rest
}) => {
  const isUserAdmin = useIsUserAdmin();
  const optionsToUse = [...options];
  const { setFieldValue, setFieldTouched, values } = useFormikContext();
  const fieldNameToUse = getFieldNameByFollowingTheGivenPathInFormikValues(
    pathToFollowInValues,
    fieldName
  );
  const selectedOptions = getIn(values, fieldNameToUse) || [];
  const handleOptionClick = async (optionValue, event, options) => {
    event.preventDefault();
    event.stopPropagation();

    const selectedOptions = getIn(values, fieldNameToUse) || [];
    const newSelectedOptions = getNewSelectedOptions(optionValue, options, selectedOptions);

    flushSync(() => {
      setFieldTouched(fieldNameToUse, true);
      setFieldValue(fieldNameToUse, newSelectedOptions);
      // Call the onChange event. We need to call this manually as we are manipulating the data ourselves and Semantic Dropdown would not know about it
      onChange(optionValue);
    });
  };

  /**
   * Modify optionsToConsider in the disabled state.
   * In the disabled state,
   *    -> we should only show the selected options.
   *    -> In case of 'all', since, 'all' is not present in the options list,
   *       we have to return the all dropdown option ourselves.
   *    -> By passing the all dropdown option, we will also make optionsToConsider array not empty,
   *       which will resolve the 'ALL' not appearing for connector_sku dimension after saving the MLD row
   *       because of the checks in getDropdownText function.
   *
   *
   * Why the 'all' case needs to be handled now?
   *    -> With connect_smt_type changes, we introduced a new column smt_type for connect task price metric,
   *       Because of the new column, when we enter values for all 3 columns(provider, smt_type, connector_sku),
   *       and then, try changing the provider, then the page was breaking. This is happening as parents are not set,
   *       dropdown options for connector_sku are being set to '[]' because of which getSingleSelectedOptionDisplayName is breaking
   *       at .display_name. To fix this, in getDropdownText function, optionsToConsider.length === 0 check has been added,
   *       which broke the 'all' scenario i.e., if 'all' is selected for connector_sku, then post saving the mld row,
   *       placeholder value of 'Connector SKU' is being shown instead of 'ALL'
   *
   * */
  const getOptionsToConsiderWhenDisabled = () => {
    if (doesArrayContainAll(selectedOptions)) {
      return getDropdownOptionsFromArray([ALL_IN_SMALL_CASE], true);
    }

    return options.filter((x) => selectedOptions.includes(x.name));
  };

  const optionsToConsider = disabled
    ? getOptionsToConsiderWhenDisabled()
    : getMultiSelectOptionsIncludingAllAndEnableAllIfApplicable(optionsToUse, isUserAdmin);

  // todo::SP Fix the issue where individual letters are selected when the user clicks on open space between the elems
  // to see this, change the color of StyledLabel to Black and click on white space

  const optionsWithCheckboxes = optionsToConsider.map((x) => {
    const isSelected = selectedOptions.includes(x.name);
    const isOptionDisabled = disabled || Boolean(x?.shouldNotBeSelectable);
    return {
      ...x,
      key: `option-${x.name}`,
      text: x.display_name,
      value: null, // This prevents automatic selection
      content: (
        <StyledLabel
          content={x.display_name}
          disabled={isOptionDisabled}
          fluid={true}
          icon={getIcon(
            isOptionDisabled,
            isSelected,
            x.display_name,
            selectedOptions,
            optionsToUse
          )}
          onClick={
            isOptionDisabled
              ? (event) => {
                  // Don't do anything when a disabled item is clicked
                  event.preventDefault();
                  event.stopPropagation();
                }
              : (event) => handleOptionClick(x.name, event, optionsToUse)
          }
          onMouseDown={(event) => {
            // Prevent this element from receiving focus when clicked
            event.preventDefault();
          }}
          size="large"
          style={{
            color: 'black',
            display: 'flex',
            alignItems: 'center',
            cursor: isOptionDisabled ? 'default' : 'pointer', // Change cursor when disabled
            width: '100%',
            opacity: isOptionDisabled ? 0.6 : 1, // Reduce opacity when disabled
            pointerEvents: isOptionDisabled ? 'none' : 'auto', // Disable pointer events when disabled
            padding: '1rem',
          }}
        />
      ),
    };
  });

  const getDropdownText = () => {
    /**
     * If either selected options length is zero or there are no dropdown options, then, display the placeholder or empty string
     * optionsToConsider will be an empty array in 2 cases:
     * 1. When BE sends empty array for the dropdown options
     * 2. When any of the parent fields are not set, then the dropdown options are set to '[]' in the DependentDimensionsDropdownContainer component
     */
    if (selectedOptions.length === 0 || optionsToConsider.length === 0) {
      return <StyledSpan>{addPlaceHolder ? fieldDisplayName : ''}</StyledSpan>;
    }
    // If only one option is selected, e.g., [All] or [ABC], then just display that as is
    if (selectedOptions.length === 1) {
      const singleSelectedOptionDisplayName = getSingleSelectedOptionDisplayName(
        selectedOptions,
        optionsToConsider
      );
      return <StyledSpan>{singleSelectedOptionDisplayName}</StyledSpan>;
    }
    // For more than one option, show {num Selected}
    return <StyledSpan> {`${selectedOptions.length} selected`} </StyledSpan>;
  };

  return (
    <SelectFieldForMultiSelectWithCheckboxes
      addErrorMessage={addErrorMessage}
      addLabel={addLabel}
      addPlaceHolder={addPlaceHolder}
      disabled={disabled}
      fieldDisplayName={fieldDisplayName}
      fieldName={fieldName}
      fluid={true}
      multiple={false} // Set to false to prevent automatic filtering..
      noResultsMessage="No more selectable options..."
      onFocus={(e) => {
        e.target.blur();
      }}
      options={optionsWithCheckboxes}
      pathToFollowInValues={pathToFollowInValues}
      showErrorsOnlyWhenTouched={showErrorsOnlyWhenTouched}
      text={getDropdownText()}
      tooltip={tooltip}
      value={null} // Set value to null to prevent automatic selection
      {...rest}
    />
  );
};

const SelectFieldForMultiSelectWithCheckboxes = ({
  fieldName,
  fieldDisplayName,
  tooltip,
  options,
  addLabel = true,
  addErrorMessage = true,
  addPlaceHolder = true,
  search = false,
  pathToFollowInValues = [],
  showErrorsOnlyWhenTouched = true,
  disabled = false,
  ...rest
}) => {
  const fieldNameToUse = getFieldNameByFollowingTheGivenPathInFormikValues(
    pathToFollowInValues,
    fieldName
  );

  return (
    <FormField>
      {addLabel && <LabelContainer fieldDisplayName={fieldDisplayName} tooltip={tooltip} />}

      <Field
        clearable={!disabled} // Disable clear when disabled
        closeOnBlur={true}
        closeOnEscape={true}
        component={StyledDropdownWithAccessibilityForMultiSelectWithCheckboxes}
        name={fieldNameToUse}
        options={getFormattedOptionsFromOptionsArray(options)}
        placeholder={addPlaceHolder ? fieldDisplayName : null}
        search={disabled ? false : search} // Disable search when disabled
        selectOnBlur={false}
        selection={true}
        {...rest}
        className={disabled ? 'disabled-multi-select-with-checkboxes-class-name' : ''}
        disabled={false} // This field should never be disabled
      />
      {addErrorMessage && (
        <StyledFormikErrorMessage
          name={fieldNameToUse}
          showErrorsOnlyWhenTouched={showErrorsOnlyWhenTouched}
        />
      )}
    </FormField>
  );
};

export const SelectField = ({
  fieldName,
  fieldDisplayName,
  tooltip,
  options,
  addLabel = true,
  addErrorMessage = true,
  addPlaceHolder = true,
  search = false,
  pathToFollowInValues = [],
  showErrorsOnlyWhenTouched = true,
  ...rest
}) => {
  const fieldNameToUse = getFieldNameByFollowingTheGivenPathInFormikValues(
    pathToFollowInValues,
    fieldName
  );

  return (
    <FormField>
      {addLabel && <LabelContainer fieldDisplayName={fieldDisplayName} tooltip={tooltip} />}

      <Field
        clearable={false}
        closeOnBlur={true}
        closeOnEscape={true}
        component={StyledDropdownWithAccessibility}
        name={fieldNameToUse}
        options={getFormattedOptionsFromOptionsArray(options)}
        placeholder={addPlaceHolder ? fieldDisplayName : null}
        search={search}
        selection={true}
        {...rest}
      />
      {addErrorMessage && (
        <StyledFormikErrorMessage
          name={fieldNameToUse}
          showErrorsOnlyWhenTouched={showErrorsOnlyWhenTouched}
        />
      )}
    </FormField>
  );
};

export const BooleanSelectField = ({ fieldName, fieldDisplayName, ...rest }) => {
  const yesNoOptions = [
    {
      name: true,
      display_name: YES,
    },
    {
      name: false,
      display_name: NO,
    },
  ];

  return (
    <SelectField
      fieldDisplayName={fieldDisplayName}
      fieldName={fieldName}
      options={yesNoOptions}
      type={BOOLEAN_SELECT_INPUT_TYPE}
      {...rest}
    />
  );
};

export const CheckBoxField = ({
  fieldName,
  fieldDisplayName,
  icon,
  onChange = () => {},
  tooltip = null,
  addLabel = true,
  addErrorMessage = true,
  pathToFollowInValues = [],
  displayCheckBoxBelowLabel = false,
  ...rest
}) => {
  const fieldNameToUse = getFieldNameByFollowingTheGivenPathInFormikValues(
    pathToFollowInValues,
    fieldName
  );

  return (
    <>
      {addLabel && <LabelContainer fieldDisplayName={fieldDisplayName} tooltip={tooltip} />}
      {displayCheckBoxBelowLabel && <br />}
      <Field
        component={StyledCheckboxWithAccessibility}
        icon={icon}
        name={fieldNameToUse}
        onChange={onChange}
        toggle={true}
        type="checkbox"
        {...rest}
      />
      {addErrorMessage && <StyledFormikErrorMessage name={fieldNameToUse} />}
    </>
  );
};

// todo:: Replace all *WithAccessibility with *
